import { useState, useEffect, createContext, useCallback } from "react";
import { User, Auth } from "../api";
import { hasexpiredToken, getTokenExpiryMs } from "../Utils";
import { Loader } from "semantic-ui-react";

const usercontroller = new User();
const authcontroller = new Auth();

// Margen con el que se renueva el token antes de que caduque. Sin esto, la
// sesion muere en caliente: el terapeuta puede estar impartiendo una terapia con
// un paciente puesto las gafas cuando el servidor empieza a devolver 403.
const RENEW_MARGIN_MS = 5 * 60 * 1000;

// setTimeout desborda por encima de 2^31-1 ms y dispararia de inmediato.
const MAX_TIMEOUT_MS = 2147483647;

export const AuthContext = createContext();

export function AuthProvider(props) {
  const { children } = props;
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  const login = useCallback(async (accessToken, userData = null) => {
    try {
      if (!userData) {
        console.log("Cargando datos del usuario desde la API con el token...");
        userData = await usercontroller.getMe(accessToken);
      }
      delete userData.password; // Eliminar la contraseña antes de guardar
      setUser(userData);
      setToken(accessToken);
    } catch (error) {
      console.error("Error al autenticar:", error);
    }
  }, []);

  const reLogin = useCallback(
    async (refreshToken) => {
      try {
        const { accessToken } =
          await authcontroller.refreshAccessToken(refreshToken);
        authcontroller.setAccessToken(accessToken);
        await login(accessToken);
      } catch (error) {
        console.error(error);
      }
    },
    [login],
  );

  const logout = useCallback(() => {
    authcontroller.removeTokens();
    setUser(null);
    setToken(null);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const accessToken = authcontroller.getAccessToken();
        const refreshToken = authcontroller.getRefreshToken();
        if (!accessToken || !refreshToken) {
          logout();
          return;
        }
        if (hasexpiredToken(accessToken)) {
          if (hasexpiredToken(refreshToken)) {
            logout();
          } else {
            await reLogin(refreshToken);
          }
        } else {
          await login(accessToken);
        }
      } catch (error) {
        // Un token corrupto en localStorage hacia saltar aqui y, como
        // setLoading(false) vivia dentro del try, la aplicacion se quedaba
        // colgada en "Cargando..." para siempre. Se cierra sesion y se sigue.
        console.error("Error restaurando la sesion:", error);
        logout();
      } finally {
        // Pase lo que pase, la aplicacion tiene que arrancar.
        setLoading(false);
      }
    })();
  }, [reLogin, login, logout]);

  // Renovacion proactiva: se programa un refresco para RENEW_MARGIN_MS antes de
  // que caduque el access token. Al renovar cambia `token`, este efecto se
  // vuelve a ejecutar y encadena el siguiente refresco.
  useEffect(() => {
    if (!token) return undefined;

    const expiresAt = getTokenExpiryMs(token);
    if (!expiresAt) return undefined;

    const delay = Math.min(
      Math.max(0, expiresAt - Date.now() - RENEW_MARGIN_MS),
      MAX_TIMEOUT_MS,
    );

    const timer = setTimeout(async () => {
      const refreshToken = authcontroller.getRefreshToken();
      if (!refreshToken || hasexpiredToken(refreshToken)) {
        logout();
        return;
      }
      await reLogin(refreshToken);
    }, delay);

    return () => clearTimeout(timer);
  }, [token, reLogin, logout]);

  const data = { accessToken: token, user, login, logout };

  if (loading) {
    return (
      <div
        style={{
          height: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Loader active inline="centered" size="large">
          Cargando...
        </Loader>
      </div>
    );
  }

  return <AuthContext.Provider value={data}>{children}</AuthContext.Provider>;
}
